import { useState } from 'react';
import { ChevronDown, ChevronUp, CalendarPlus, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useCreateSession, useUpdateSession } from '@/lib/hooks/useTrainer';
import type { ModuleWithProgress } from '@/lib/api/lms';
import type { UpcomingSession } from '@/lib/api/trainer';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/cn';

const TIME_SLOTS = Array.from({ length: 33 }, (_, i) => {
  const mins = 360 + i * 30; // 06:00 → 22:00
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
});

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

interface TopicComboboxProps {
  modules: ModuleWithProgress[];
  value: string;
  sessionsByTopic: Map<string, UpcomingSession>;
  onChange: (id: string, topicTitle: string, session: UpcomingSession | undefined) => void;
}

function TopicCombobox({ modules, value, sessionsByTopic, onChange }: TopicComboboxProps) {
  const [open, setOpen] = useState(false);

  const selectedLabel = (() => {
    for (const m of modules) {
      const t = m.topics.find((t) => t.id === value);
      if (t) return `${m.title} / ${t.title}`;
    }
    return null;
  })();

  return (
    <div className="relative">
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring',
          !selectedLabel && 'text-[#6b7280]',
        )}
      >
        <span className="truncate">{selectedLabel ?? 'Select a topic…'}</span>
        <ChevronDown className={cn('ml-2 h-4 w-4 shrink-0 opacity-50 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-[#ececec] bg-popover shadow-md">
          <Command>
            <CommandInput placeholder="Search topics…" />
            <CommandList className="max-h-72">
              <CommandEmpty>No topics found.</CommandEmpty>
              {modules.map((m) => (
                <CommandGroup key={m.id} heading={m.title}>
                  {m.topics.map((t) => {
                    const session = sessionsByTopic.get(t.id);
                    return (
                      <CommandItem
                        key={t.id}
                        value={`${m.title} ${t.title}`}
                        onSelect={() => {
                          onChange(t.id, t.title, session);
                          setOpen(false);
                        }}
                      >
                        <Check className={cn('mr-2 h-4 w-4 shrink-0', t.id === value ? 'opacity-100' : 'opacity-0')} />
                        <span className="flex-1 truncate">{t.title}</span>
                        {session && (
                          <span className="ml-2 shrink-0 rounded-full bg-[#fff1ec] px-2 py-0.5 text-[10px] font-medium text-[#ff5a1f]">
                            {fmtTime(session.scheduled_at)}
                          </span>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
}

interface ScheduleSessionFormProps {
  batchId: string;
  modules: ModuleWithProgress[];
  sessions?: UpcomingSession[];
  forceOpen?: boolean;
  initialTopicId?: string;
  onSuccess?: () => void;
}

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function nextTimeSlot() {
  const now = new Date();
  const mins = Math.ceil((now.getHours() * 60 + now.getMinutes()) / 30) * 30;
  if (mins < 360 || mins > 1320) return '09:00'; // outside 06:00–22:00
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
}

export function ScheduleSessionForm({ modules, sessions = [], forceOpen = false, initialTopicId, onSuccess }: ScheduleSessionFormProps) {
  const [open, setOpen] = useState(false);
  const [batchTopicId, setBatchTopicId] = useState(initialTopicId ?? '');
  const [selectedSession, setSelectedSession] = useState<UpcomingSession | undefined>();
  const [title, setTitle] = useState(() => {
    if (!initialTopicId) return '';
    const topic = modules.flatMap((m) => m.topics).find((t) => t.id === initialTopicId);
    return topic?.title ?? '';
  });
  const [scheduledDate, setScheduledDate] = useState(todayStr);
  const [scheduledTime, setScheduledTime] = useState(nextTimeSlot);
  const [meetingUrl, setMeetingUrl] = useState('');
  const [recordingUrl, setRecordingUrl] = useState('');
  const [durationMins, setDurationMins] = useState('');

  // first session per topic (for indicator + auto-fill)
  const sessionsByTopic = new Map(sessions.map((s) => [s.topic_id, s]));

  const createSession = useCreateSession();
  const updateSession = useUpdateSession();

  const reset = () => {
    setBatchTopicId('');
    setSelectedSession(undefined);
    setTitle('');
    setScheduledDate(todayStr());
    setScheduledTime(nextTimeSlot());
    setMeetingUrl('');
    setRecordingUrl('');
    setDurationMins('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!batchTopicId || !title || !scheduledDate || !scheduledTime) {
      toast.error('Please fill in all required fields.');
      return;
    }
    const scheduled_at = new Date(`${scheduledDate}T${scheduledTime}:00`).toISOString();
    try {
      if (selectedSession) {
        await updateSession.mutateAsync({
          id: selectedSession.id,
          req: {
            title,
            scheduled_at,
            meeting_url: meetingUrl,
            recording_url: recordingUrl,
            duration_mins: durationMins ? parseInt(durationMins, 10) : undefined,
          },
        });
        toast.success('Session updated.');
      } else {
        await createSession.mutateAsync({
          batch_topic_id: batchTopicId,
          title,
          scheduled_at,
          meeting_url: meetingUrl,
          duration_mins: durationMins ? parseInt(durationMins, 10) : undefined,
        });
        toast.success('Session scheduled successfully.');
      }
      if (!forceOpen) setOpen(false);
      reset();
      onSuccess?.();
    } catch {
      toast.error(selectedSession ? 'Failed to update session.' : 'Failed to schedule session.');
    }
  };

  return (
    <div className={forceOpen ? undefined : 'rounded-lg border border-[#ececec] bg-white'}>
      {!forceOpen && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium hover:bg-[#fafafa] transition-colors rounded-lg"
        >
          <span className="flex items-center gap-2">
            <CalendarPlus className="h-4 w-4 text-[#ff5a1f]" />
            Schedule New Session
          </span>
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      )}

      {(open || forceOpen) && (
        <form
          onSubmit={handleSubmit}
          className={forceOpen ? 'space-y-3' : 'border-t border-[#ececec] px-4 py-4 space-y-3'}
        >
          <div>
            <label className="mb-1 block text-xs font-medium text-[#6b7280]">
              Topic <span className="text-destructive">*</span>
            </label>
            <TopicCombobox
              modules={modules}
              value={batchTopicId}
              sessionsByTopic={sessionsByTopic}
              onChange={(id, topicTitle, session) => {
                const prevTopic = modules.flatMap((m) => m.topics).find((t) => t.id === batchTopicId);
                const prevTitle = selectedSession ? selectedSession.title : (prevTopic?.title ?? '');
                setBatchTopicId(id);
                setSelectedSession(session);
                if (!title || title === prevTitle) setTitle(session ? session.title : topicTitle);
                if (session) {
                  const d = new Date(session.scheduled_at);
                  setScheduledDate(d.toLocaleDateString('en-CA'));
                  const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                  setScheduledTime(hhmm);
                  setDurationMins(session.duration_mins != null ? String(session.duration_mins) : '');
                  setMeetingUrl(session.meeting_url ?? '');
                } else {
                  setDurationMins('');
                  setMeetingUrl('');
                }
              }}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-[#6b7280]">
              Session Title <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Live session — React Hooks"
              required
              className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-[#6b7280]">
                Date <span className="text-destructive">*</span>
              </label>
              <DatePicker value={scheduledDate} onChange={setScheduledDate} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-[#6b7280]">
                Time <span className="text-destructive">*</span>
              </label>
              <Select value={scheduledTime} onValueChange={setScheduledTime}>
                <SelectTrigger>
                  <SelectValue placeholder="Select time…" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-[#6b7280]">
                Duration (mins)
              </label>
              <input
                type="number"
                value={durationMins}
                onChange={(e) => setDurationMins(e.target.value)}
                placeholder="90"
                min="1"
                className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-[#6b7280]">
                Meeting URL
              </label>
              <input
                type="url"
                value={meetingUrl}
                onChange={(e) => setMeetingUrl(e.target.value)}
                placeholder="https://meet.google.com/..."
                className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            {!forceOpen && (
              <button
                type="button"
                onClick={() => { setOpen(false); reset(); }}
                className="rounded-md px-3 py-1.5 text-sm text-[#6b7280] hover:text-[#111827] transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={createSession.isPending || updateSession.isPending}
              className="rounded-md bg-[#ff5a1f] px-4 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 transition-colors"
            >
              {selectedSession
                ? (updateSession.isPending ? 'Updating…' : 'Update Session')
                : (createSession.isPending ? 'Scheduling…' : 'Schedule Session')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
